/* @jsx h */
import { defineComponent, ref, onMounted, h, computed, watch } from "vue";
import { api } from "@app/data/api";
import { TipsDatum } from "@lemmonhq/types";
import {
  TipsFilters,
  buildTipsFilterSummary,
  countActiveTipsFilters,
  type TipsFiltersProps,
} from "./TipsFilters";
import { ViewMode } from "./tips-types";
import {
  getDateRange,
  syncedRef,
  useLocalization,
  PageHeader,
} from "@lemmonhq/uikit";
import { TipsSummary } from "./TipsSummary";
import { useAuth } from "@app/vue/providers/Auth";
import { sharedDateRangeKey } from "@app/vue/composables/sharedDateRangeKey";

export const TipsSummaryPage = defineComponent({
  name: "TipsSummaryPage",
  setup() {
    const { t } = useLocalization();
    const auth = useAuth();
    const loading = ref(true);
    const tips = ref<TipsDatum[]>([]);

    // Shared with the other report pages, so the range picked here is the one
    // they open on (and vice versa).
    const dateRange = syncedRef(
      sharedDateRangeKey(
        auth.user.value?.restaurant_id,
        auth.user.value?.id,
      ),
      {
        type: "today",
        ...getDateRange("today"),
      },
    );
    const viewMode = ref<ViewMode>("user");

    const filters = computed<TipsFiltersProps["filters"]>({
      get: () => ({ dateRange: dateRange.value, viewMode: viewMode.value }),
      set: (next) => {
        dateRange.value = next.dateRange;
        viewMode.value = next.viewMode;
      },
    });

    const fetchData = async () => {
      loading.value = true;
      try {
        const [tipsData] = await Promise.all([
          api.getTips({
            p_start_date: filters.value.dateRange.startDate,
            p_end_date: filters.value.dateRange.endDate,
          }),
        ]);
        tips.value = tipsData;
      } catch (error) {
        console.error("Failed to fetch Tips data:", error);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchData();
    });

    // View mode is a client-side regrouping, so only the range refetches.
    watch(dateRange, fetchData);

    const filterSummary = computed(() =>
      buildTipsFilterSummary(filters.value, t),
    );
    const activeFilterCount = computed(() =>
      countActiveTipsFilters(filters.value),
    );

    return () => (
      <div class="flex h-full w-full min-w-0 flex-1 flex-col overflow-hidden">
        <PageHeader
          title={t("reports.nav.tips")}
          filterSummary={filterSummary.value}
          activeFilterCount={activeFilterCount.value}
          filterSheetTitle={t("reports.tips_nav_words.filters")}
        >
          {{
            default: ({
              closeMobileSheet,
            }: {
              closeMobileSheet: () => void;
            }) => (
              <TipsFilters
                filters={filters.value}
                onFiltersChange={(newFilters) => {
                  filters.value = newFilters;
                }}
                onApply={closeMobileSheet}
              />
            ),
          }}
        </PageHeader>

        <div class="min-h-0 flex-1 overflow-y-auto py-4">
          <TipsSummary
            tips={tips.value}
            loading={loading.value}
            viewMode={filters.value.viewMode}
          />
        </div>
      </div>
    );
  },
});
